// Which rows sit in the bank, and how old they are.
//
// Show days since Scout last wrote the account. When only an assignment timestamp
// exists, name that fallback explicitly; neither timestamp is Salesforce CreatedDate.

import type { BookAccount, BookPayload } from '../api/types';
import { daysSince, pacificCalendarDay } from '../lib/format.ts';

// The bank's address, or the bank plus its aliases (`functions.BANK_ALIAS_EMAILS`: the Scout
// Integration user). Either shape is accepted so one address still works where only it is known.
export type BankEmails = string | readonly string[] | null | undefined;

// Every address that counts as the bank, lowercased. Prefers the payload's alias list.
export function bankEmailsOf(book: Pick<BookPayload, 'bank_owner_email' | 'bank_owner_emails'> | null | undefined): string[] {
  if (!book) return [];
  if (book.bank_owner_emails && book.bank_owner_emails.length) return bankEmailList(book.bank_owner_emails);
  return bankEmailList(book.bank_owner_email);
}

function bankEmailList(bankEmails: BankEmails): string[] {
  const emails = typeof bankEmails === 'string' ? [bankEmails] : (bankEmails ?? []);
  return emails.map(email => email.trim().toLowerCase()).filter(Boolean);
}

// True when this owner address is the bank or one of its aliases. Empty is never the bank.
export function isBankEmail(email: string | null | undefined, bankEmails: BankEmails): boolean {
  const owner = (email || '').trim().toLowerCase();
  return Boolean(owner) && bankEmailList(bankEmails).includes(owner);
}

export function isBankOwned(row: BookAccount, bankEmails: BankEmails): boolean {
  return isBankEmail(row.sdr_owner_email, bankEmails);
}

// A retired account (plans/retire-cold-accounts.md) is in the bank but not workable: it is
// watched, and comes back on its own when a real signal wakes it, so the bank never lists it.
export function isRetired(row: BookAccount): boolean {
  return (row.scout_state ?? '').trim().toLowerCase() === 'retired';
}

export function bankRows(
  rows: BookAccount[],
  bankEmails: BankEmails,
): BookAccount[] {
  return rows.filter(row => isBankOwned(row, bankEmails) && !isRetired(row));
}

export function bankAgeDays(row: BookAccount, today: Date): number | null {
  const stamp = row.scout_last_written_at || row.scout_assigned_at;
  if (!stamp) return null;
  return daysSince(pacificCalendarDay(stamp), today);
}

export function bankAgeLabel(row: BookAccount, today: Date): string {
  const age = bankAgeDays(row, today);
  if (age == null) return '—';
  const label = age === 0 ? 'today' : age === 1 ? '1 day ago' : `${age} days ago`;
  return row.scout_last_written_at ? label : `Assigned ${label}`;
}

// The age for a one-line cell: "today", "3d", or a dash. The assignment fallback is marked
// "Assigned" in the hover (bankScoutTitle), not here.
export function bankAgeShort(row: BookAccount, today: Date): string {
  const age = bankAgeDays(row, today);
  if (age == null) return '—';
  return age === 0 ? 'today' : `${age}d`;
}

// The hover for that cell: the full age and the state. Source has its own column.
export function bankScoutTitle(row: BookAccount, today: Date): string {
  const parts: string[] = [];
  const age = bankAgeLabel(row, today);
  if (age !== '—') parts.push(row.scout_last_written_at ? `Scout updated ${age}` : age);
  if (row.scout_state) parts.push(`State: ${row.scout_state}`);
  return parts.join(' · ');
}

// Scout's read for the Available and My requests tables: Scout's one-line fit reason, else the
// saved company research's summary. An account loaded from the Q4 lists never went through the
// scorer, so its Scout_Fit_Reason__c is empty while its research (copied 20 Oct) is not.
export function scoutRead(row: BookAccount | null | undefined): string | null {
  if (!row) return null;
  const reason = row.scout_fit_reason?.trim();
  if (reason) return reason;
  return row.company_research?.summary?.trim() || null;
}
